// @flow

import React, { useState, useEffect, useMemo } from "react";
import Toggle from "react-toggle";
import { Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { translate } from "../IntlProviderWrapper";
import ErrorComponent from "../ErrorComponent";

import { getBackend } from "../../lib/backend/Backend2";
import * as u from "../../lib/Util";
import * as t from "../../lib/backend/manufacturing2.generated.types";
import * as t2 from "../../lib/backend/reports.generated.types";

/** Assignments for single line */
export type LineAssignments = {
    line_uuid: string,
    persons: t.IShiftPersonLineRecPerson[],
};

type GetUniquePersonNameFunction = string => string;

/** Result of this modal page */
export type PoolEditModalType = {
    /** Flag if user wants information to be persisted */
    hasConfirmed: boolean,
    /** New assignments */
    assignments: LineAssignments[],
    /** Shift index for which these assignments are to be updated */
    shift_idx: number,
    /** New hash returned after databse save */
    hash: string,
};

/** Callback type when modal closes */
export type OnClosePoolEditModalType = PoolEditModalType => void;

type Props = {
    show: boolean,
    onClose: OnClosePoolEditModalType,
    pool_line: t.IShiftPersonLineRec,
    lines: t.IShiftPersonLineRec[],
    lines_pooled: t.IShiftPersonLineRec[],
    shift_idx: number,
    shift_tag: string,
    hash: string,
    uniquePersonName: GetUniquePersonNameFunction,
};

type State = {
    selected_person: t.IShiftPersonLineRecPerson,
    assignments: LineAssignments[],
    weekly_rep: t2.IWeeklyRealizationReport[],
    worker_hours: Map<string, number>,
    assigned_workers: Set<string>,
    show_disabled_shifts: boolean,
    errorMsg: ?string,
    week: number,
    year: number,
};

const initialState: State = {
    selected_person: { name: "", uuid: "", id: 0 },
    assignments: [],
    weekly_rep: [],
    worker_hours: new Map(),
    assigned_workers: new Set(),
    show_disabled_shifts: false,
    errorMsg: null,
    week: -1,
    year: -1,
};

const createShiftMap = (lines: t.IShiftPersonLineRec[], shift_tag: string): Map<string, t.IShiftPersonLineRecItem> => {
    const shift_map: Map<string, t.IShiftPersonLineRecItem> = new Map();
    lines.forEach(line => {
        const shift: t.IShiftPersonLineRecItem | void = line.shifts.find(s => s.shift_tag === shift_tag);
        if (shift) {
            shift_map.set(line.line_uuid, shift);
        }
    });
    return shift_map;
};

const ShiftAssignmentPoolEdit = (props: Props) => {
    const [state, setState] = useState(initialState);

    const all_shifts_map = useMemo(() => createShiftMap(props.lines, props.shift_tag), [props.lines, props.shift_tag]);

    const pooled_shifts_map = useMemo(
        () => createShiftMap(props.lines_pooled, props.shift_tag),
        [props.lines_pooled, props.shift_tag]
    );

    const pool_line_shift: t.IShiftPersonLineRecItem | void = useMemo(
        () => props.pool_line.shifts.find(s => s.shift_tag === props.shift_tag),
        [props.pool_line.shifts, props.shift_tag]
    );

    const calculateWorkerHours = (assignments: LineAssignments[]): Map<string, number> => {
        const new_worker_hours: Map<string, number> = new Map();

        props.lines_pooled.forEach(line => {
            const shift = pooled_shifts_map.get(line.line_uuid);
            const assignment = assignments.find(x => x.line_uuid === line.line_uuid);

            if (assignment && assignment.persons.length) {
                const hours_per_person = (shift?.worker_hours ?? 0) / assignment.persons.length;

                assignment.persons.forEach(person => {
                    const prev_val = new_worker_hours.get(person.uuid) ?? 0;
                    new_worker_hours.set(person.uuid, prev_val + hours_per_person);
                });
            }
        });

        return new_worker_hours;
    };

    const calculateAssignedWorkers = (assignments: LineAssignments[]): Set<string> => {
        const new_assigned_workers: Set<string> = new Set();
        const pooled_lines_uuids: Set<string> = new Set(props.lines_pooled.map(l => l.line_uuid));

        const pooled_lines_assignments = assignments.filter(l => pooled_lines_uuids.has(l.line_uuid));

        pooled_lines_assignments.forEach(assignment => {
            assignment.persons.forEach(person => {
                new_assigned_workers.add(person.uuid);
            });
        });

        return new_assigned_workers;
    };

    useEffect(() => {
        if (!props.show) {
            return;
        }

        const shift_number = u.shiftNumber(u.fromShiftTag(props.shift_tag));

        const new_assignments = props.lines.map(line => {
            let persons = [];
            const line_shift = all_shifts_map.get(line.line_uuid);

            if (line_shift !== undefined) {
                persons = [...line_shift.persons];
            }
            return { line_uuid: line.line_uuid, persons };
        });

        const new_worker_hours = calculateWorkerHours(new_assignments);
        const new_assigned_workers = calculateAssignedWorkers(new_assignments);

        setState({
            ...state,
            errorMsg: "",
            week: shift_number.week,
            year: shift_number.year,
            assignments: new_assignments,
            worker_hours: new_worker_hours,
            assigned_workers: new_assigned_workers,
        });
    }, [props.show, props.lines, props.shift_tag, all_shifts_map]);

    useEffect(() => {
        if (props.show && state.week !== -1) {
            loadWorkOrders();
        }
    }, [props.show, state.week, state.year, props.shift_idx]);

    const loadWorkOrders = async () => {
        if (props.show) {
            try {
                const res = await getBackend().reports.searchReports({ num_reports: 1 });
                if (res.reports && res.reports.length > 0) {
                    const report_uuid = res.reports[0].uuid;
                    const report = await getBackend().reports.getReportWeeklyRealization({
                        id: report_uuid,
                        line_uuids: props.lines_pooled.map(x => x.line_uuid).join(","),
                        order_breakdown: true,
                        shift_number: props.shift_idx,
                        week: state.week,
                    });
                    state.weekly_rep = report.report.result;
                    setState({ ...state });
                }
            } catch (err) {
                setState({ ...state, errorMsg: err.message });
            }
        }
    };

    const getWorkOrdersForLine = (line_uuid: string) => {
        const orders = state.weekly_rep.filter(x => x.line_uuid === line_uuid);
        return orders.map(order => (
            <div key={"order-" + order.order_uuid}>
                <span
                    className={"badge badge-pill badge-secondary"}
                    title={translate("ShiftAssignmentPoolEdit.order", "Order")}
                >
                    {order.order_external_id}
                </span>{" "}
                <span
                    className={"badge badge-pill badge-info"}
                    title={translate("ShiftAssignmentPoolEdit.material", "Material")}
                >
                    {order.material_external_id}
                </span>{" "}
                {order.material_title}
            </div>
        ));
    };

    const onConfirm = async () => {
        try {
            props.onClose({
                hasConfirmed: true,
                assignments: state.assignments,
                shift_idx: props.shift_idx,
                hash: props.hash,
            });
        } catch (e) {
            setState({ ...state, errorMsg: e.message });
        }
    };

    const onCancel = () => {
        props.onClose({ hasConfirmed: false, assignments: [], shift_idx: -1, hash: "" });
    };

    const assignOrRemoveCurrent = (line_uuid: string) => {
        if (state.selected_person.uuid === "") {
            return;
        }

        setState(prev_state => {
            const new_assignments = [...prev_state.assignments];
            const line = new_assignments.find(x => x.line_uuid === line_uuid);

            if (line) {
                const person = line.persons.find(x => x.uuid === prev_state.selected_person.uuid);

                if (person) {
                    line.persons = line.persons.filter(x => x.uuid !== prev_state.selected_person.uuid);
                } else {
                    line.persons = [...line.persons, prev_state.selected_person];
                }

                const new_worker_hours = calculateWorkerHours(new_assignments);
                const new_assigned_workers = calculateAssignedWorkers(new_assignments);

                return {
                    ...prev_state,
                    assignments: new_assignments,
                    worker_hours: new_worker_hours,
                    assigned_workers: new_assigned_workers,
                };
            }

            return prev_state;
        });
    };

    let bullet_key_counter = 0;
    const createBullet = () => {
        const tooltip = translate("ShiftAssignmentPoolEdit.icon_text.too_many_hours", "Too many working hours");
        const key = "bullet" + bullet_key_counter++;
        return (
            <span key={key} className={"badge badge-pill badge-danger pool-worker-alert"} title={tooltip}>
                {"!"}
            </span>
        );
    };

    const alertWorkerHours = (person: string) => {
        const worker_hours_thr = u.SHIFT_CONSTS.SHIFT_DURATION_HOURS;
        if ((state.worker_hours.get(person) ?? 0) > worker_hours_thr) {
            return createBullet();
        }
    };

    const renderLines = () => {
        if (!props.show) {
            return null;
        }

        const lines = props.lines_pooled;
        return (
            <table className="table table-hover table-sm">
                <thead className="thead-light">
                    <tr>
                        <th>{translate("ShiftAssignmentPoolEdit.persons", "Persons")}</th>
                        <th>{translate("ShiftAssignmentPoolEdit.line", "Line")}</th>
                        <th>{translate("ShiftAssignmentPoolEdit.work_hours", "Work-hours")}</th>
                        <th>{translate("ShiftAssignmentPoolEdit.work_orders", "Work-order(s)")}</th>
                        <th>{translate("ShiftAssignmentPoolEdit.tool_change", "Tool-change")}</th>
                    </tr>
                </thead>
                <tbody>
                    {lines.map((line, i) => {
                        const shift = pooled_shifts_map.get(line.line_uuid);
                        if (shift === undefined || (!state.show_disabled_shifts && !shift.enabled)) {
                            return null;
                        }
                        const css = shift.enabled ? "" : "pool-edit-row-disabled";
                        const line_assignment = state.assignments.find(x => x.line_uuid === line.line_uuid) || {
                            line_uuid: line.line_uuid,
                            persons: [],
                        };

                        return (
                            <tr key={"" + i} className={css} onClick={() => assignOrRemoveCurrent(line.line_uuid)}>
                                <td>
                                    {line_assignment.persons.map(person => {
                                        let css = "pool-edit-person-cell";
                                        if (person.uuid === state.selected_person.uuid) {
                                            css += " pool-edit-person-selected";
                                        }

                                        if (
                                            !pool_line_shift ||
                                            !pool_line_shift.persons.some(p => p.uuid === person.uuid)
                                        ) {
                                            css += " pool-edit-person-not-in-pool";
                                        }

                                        return (
                                            <div className={css} key={person.uuid}>
                                                <span>{person.name}</span>
                                                {alertWorkerHours(person.uuid)}
                                            </div>
                                        );
                                    })}
                                </td>
                                <td>{line.line_title}</td>
                                <td>{shift.worker_hours && u.niceNumber(shift.worker_hours, 1)}</td>
                                <td>{getWorkOrdersForLine(line.line_uuid)}</td>
                                <td>
                                    {shift.tool_changes.map((tc, idx) => (
                                        <div key={`tc-${line.line_uuid}-${idx}`}>
                                            <span className="badge badge-pill badge-info">{tc.material_ext_id}</span>{" "}
                                            {tc.title}
                                        </div>
                                    ))}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    const renderPeople = () => {
        if (!props.show) {
            return null;
        }
        const people = pool_line_shift?.persons ?? [];

        return (
            <div>
                {people.map((person, i) => {
                    let css = "pool-edit-person";
                    if (person.uuid === state.selected_person.uuid) {
                        css += " pool-edit-person-selected";
                    }
                    if (!state.assigned_workers.has(person.uuid)) {
                        css += " pool-edit-person-not-assigned";
                    }
                    return (
                        <div
                            key={"" + i}
                            className={css}
                            onClick={() => {
                                if (state.selected_person.uuid === person.uuid) {
                                    setState({ ...state, selected_person: { name: "", uuid: "", id: 0 } });
                                } else {
                                    setState({ ...state, selected_person: person });
                                }
                            }}
                        >
                            {person.name}
                        </div>
                    );
                })}
            </div>
        );
    };

    const getModalTitle = (): string => {
        const shift_date = u.fromShiftTag(props.shift_tag);
        return [
            translate("ShiftAssignmentPoolEdit.assignments", "Assignments"),
            `${translate("common.week", "week")} ${state.week}`,
            `${u.dayString(shift_date.getDay() + 6)}, ${u.niceDate(shift_date)}`,
            `${translate("common.shift", "Shift")} ${u.weekToDayShift(props.shift_idx) + 1}`,
        ].join(" - ");
    };

    return (
        <Modal
            size="lg"
            className="batchop-archive-item-modal"
            dialogClassName="modal-dialog-scrollable"
            show={props.show}
            onHide={onCancel}
        >
            <Modal.Header>
                <Modal.Title>{getModalTitle()}</Modal.Title>

                <button type="button" className="close" onClick={onCancel}>
                    <span>×</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                <React.Fragment>
                    <ErrorComponent msg={state.errorMsg ?? null} type="error" />
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-sm-3">{renderPeople()}</div>
                            <div className="col-sm-9">
                                {renderLines()}
                                <div className="row">
                                    <div className="col-sm-6">
                                        <span>
                                            <FormattedMessage
                                                id="ShiftAssignmentPoolEdit.show_lines"
                                                defaultMessage="Show lines with disabled shifts"
                                            />
                                        </span>
                                        <br />
                                        <Toggle
                                            name="toggle_show_c"
                                            checked={state.show_disabled_shifts}
                                            onChange={() =>
                                                setState({
                                                    ...state,
                                                    show_disabled_shifts: !state.show_disabled_shifts,
                                                })
                                            }
                                        />
                                    </div>
                                    <div className="col-sm-6 button-list order-last text-right max-width-50perc">
                                        <dl className="property-list shift-legend">
                                            <dt>{translate("common.legend", "Legend")}:</dt>
                                            <dt>{createBullet()}</dt>
                                            <dd>
                                                <FormattedMessage
                                                    id="ShiftAssignmentPoolEdit.icon_text.too_many_hours"
                                                    defaultMessage="Too many working hours"
                                                />
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            </Modal.Body>
            <Modal.Footer>
                <button onClick={onCancel} className="btn">
                    <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                </button>
                <button onClick={onConfirm} className="btn btn-primary">
                    <FormattedMessage id="common.save" defaultMessage="Save" />
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ShiftAssignmentPoolEdit;
